import { stellarnavFactory } from './stellarnav-factory.js';
import { expandShrinkFactory } from './expand-shrink-factory.js';
/** @typedef {import('./stellarnav-types.ts').Stellarnav} Stellarnav */
/** @typedef {import('./stellarnav-types.ts').ObjKeys} ObjKeys */
/** @typedef {import('./stellarnav-types.ts').StellarnavStrictOptions} StellarnavStrictOptions */
/** @typedef {(value: any) => boolean} TypeChecker */
/** @typedef {{[key: string]: TypeChecker}} OptionsValidator */

/** @type {TypeChecker} */
const isString = (value) => typeof value === 'string';
/** @type {TypeChecker} */
const isNumber = (value) => typeof value === 'number';
/** @type {TypeChecker} */
const isBoolean = (value) => typeof value === 'boolean';

/** @type {ObjKeys} */
const objectKeys = (obj) => /** @type {(keyof obj)[]} */ (Object.keys(obj));

/** @type {OptionsValidator} */
const optionsValidator = {
    theme: (value) => typeof value === 'string' && ['plain', 'light', 'dark'].includes(value),
    position: (value) =>
        typeof value === 'string' && ['left', 'right', 'leftfixed', 'rightfixed', 'top'].includes(value),
    breakpoint: isNumber,
    menuHidden: isBoolean,
    menuLabel: isString,
    sticky: isBoolean,
    openingSpeed: isNumber,
    closingDelay: isNumber,
    showArrows: isBoolean,
    phoneBtn: isString,
    phoneLabel: isString,
    locationBtn: isString,
    locationLabel: isString,
    closeLabel: isString,
    mobileMode: isBoolean,
    scrollbarFix: isBoolean,
    expandShrink: isBoolean,
    hoverExpand: isBoolean,
    showPopovers: isBoolean,
    isMenuExpanded: isBoolean,
    toggleOnTop: isBoolean,
    currentTabId: isString,
    currentRootTabId: isString,
    shouldShowBreadcrumbs: isBoolean,
};

$.fn.extend({
    /** @type {Stellarnav} */
    stellarNav(options) {
        // default settings
        /** @type {StellarnavStrictOptions} */
        const defaultOptions = {
            theme: 'plain', // adds default color to nav. (light, dark)
            breakpoint: 766, // number in pixels to determine when the nav should turn mobile friendly
            menuHidden: false, // custom added
            menuLabel: '', // label for the mobile nav
            sticky: false, // makes nav sticky on scroll (desktop only)
            position: 'top', // 'top', 'left', 'right', 'leftfixed', 'rightfixed' - when set to 'top', this forces the mobile nav to be placed absolutely on the very top of page
            openingSpeed: 250, // how fast the dropdown should open in milliseconds
            closingDelay: 250, // controls how long the dropdowns stay open for in milliseconds
            showArrows: true, // shows dropdown arrows next to the items that have sub menus
            phoneBtn: '', // adds a click-to-call phone link to the top of menu - i.e.: "18009084500"
            phoneLabel: '', // label for the phone button
            locationBtn: '', // adds a location link to the top of menu - i.e.: "/location/", "http://site.com/contact-us/"
            locationLabel: '', // label for the location button
            closeLabel: '', // label for the close button
            mobileMode: false,
            scrollbarFix: false, // fixes horizontal scrollbar issue on very long navs
            expandShrink: false, // remove after dev
            hoverExpand: false, // expand on hover
            showPopovers: false, // show popovers on mouseover
            isMenuExpanded: false, // control the default state of nav menu
            toggleOnTop: true, // toggle button should be on top or bottom
            currentTabId: '',
            currentRootTabId: '',
            shouldShowBreadcrumbs: false,
        };

        objectKeys(defaultOptions).forEach((key) => {
            const givenProperty = options[key];
            if (givenProperty === undefined) return;
            if (optionsValidator[key](givenProperty)) return;
            throw new TypeError(`Invalid parameter given for ${key}. Check stellarnav.js for valid values.`);
        });

        const settings = $.extend(defaultOptions, options);

        return this.each((_navIndex, navElement) => {
            const $window = $(globalThis);
            const getIsScreenMobileSize = () => {
                const width = $window.width();
                if (!width) throw new Error('Window width is undefined');
                return width < settings.breakpoint;
            };
            const $nav = $(navElement);
            const $firstUl = $nav.children('ul:first');
            const $hambuger = $('a.hambuger.stellarnav-open#desktophambugericon');

            // # region render bread crumbs
            const regionRenderBreadCrumbs = () => {
                const $breadcrumbContainer = $('#breadcrumb-container');
                const $selectedNavTabs = $nav.find(`li[data-tab-id="${settings.currentTabId}"]`);
                if (
                    settings.shouldShowBreadcrumbs &&
                    $selectedNavTabs.length > 0 &&
                    Number($selectedNavTabs.attr('data-level')) > 1 &&
                    !settings.menuHidden
                ) {
                    let breadCrumbHtml = '<ol class="breadcrumb">';
                    $nav.find('li.active').each((_index, listItem) => {
                        const $listItem = $(listItem);
                        const $navLink = $listItem.children('.nav-link');
                        const listItemText = $navLink.text().trim();

                        breadCrumbHtml += $selectedNavTabs.is($listItem)
                            ? `<li class="breadcrumb-item active" aria-current="page">${listItemText}</li>`
                            : `<li class="breadcrumb-item"><a href="${$navLink.attr('href')}">${listItemText}<a/></li>`;
                    });
                    breadCrumbHtml += '</ol>';
                    $breadcrumbContainer.html(breadCrumbHtml);
                }
            };
            // # end of region render bread crumbs
            $nav.addClass(`position-${settings.position}`);

            if ($firstUl.find('li[data-level="1"][hidden]').length === $firstUl.find('li[data-level="1"]').length) {
                $nav.hide();
                if (!settings.menuHidden) $hambuger.attr('style', 'display: none !important');
                return;
            }

            const { unfixedHorizontalModule, fixedHorizontalModule, topPositionModule, commonFixedHorizontalModule } =
                stellarnavFactory(navElement, settings);

            if (settings.position === 'top') {
                topPositionModule.setup();
                regionRenderBreadCrumbs();
                return;
            }

            if (['left', 'right'].includes(settings.position)) {
                unfixedHorizontalModule.setup();
                regionRenderBreadCrumbs();
                return;
            }

            const getModuleFromSettings = () => {
                if (!settings.expandShrink) return fixedHorizontalModule;

                const { showPopovers, hoverExpand, expandShrinkMain } = expandShrinkFactory(
                    navElement,
                    commonFixedHorizontalModule,
                    settings,
                );

                if (settings.showPopovers) return showPopovers;
                if (settings.hoverExpand) return hoverExpand;
                return expandShrinkMain;
            };

            const desktopModule = getModuleFromSettings();

            const setupModuleFromWindowSize = () => {
                if (getIsScreenMobileSize()) {
                    desktopModule.destroy();
                    unfixedHorizontalModule.setup();
                } else {
                    unfixedHorizontalModule.destroy();
                    desktopModule.setup();
                }
            };

            setupModuleFromWindowSize();
            regionRenderBreadCrumbs();
            $window.on('resize', setupModuleFromWindowSize);
        });
    },
});
